import { useState } from 'react';
import { Plus, Users, Loader2, AlertCircle, ArrowRight, Trash2, Mail } from 'lucide-react';
import { useSWRConfig } from 'swr';
import toast from 'react-hot-toast';
import { z } from 'zod';
import { useAuthStore } from '../../hooks/useAuth';
import { validatePhone } from '../../utils/fieldValidation';
import {
    Dialog,
    DialogPortal,
    DialogOverlay,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter
} from '../ui/dialog';

// Zod Schema
const customerSchema = z.object({
    name: z.string().min(2, 'Name must be at least 2 characters').trim(),
    code: z.string().max(20, 'Code too long').optional().transform(v => v?.trim() || ''),
    phone: z.string().optional(),
    status: z.enum(['Active', 'Inactive'])
});

type CustomerFormData = z.infer<typeof customerSchema>;

const CONTACT_ROLES = [
    { value: '', label: 'Role (optional)' },
    { value: 'shipping', label: 'Shipping' },
    { value: 'logistics', label: 'Logistics' },
    { value: 'purchasing', label: 'Purchasing' },
    { value: 'accounting', label: 'Accounting' },
    { value: 'general', label: 'General' },
];

interface ContactRow {
    email: string;
    name: string;
    role: string;
    isPrimary: boolean;
    receivesNotifications: boolean;
}

interface CustomerCreateDialogProps {
    onSuccess?: () => void;
    onOpenConflict?: (customerId: string) => void;
}

export function CustomerCreateDialog({ onSuccess, onOpenConflict }: CustomerCreateDialogProps) {
    const { token } = useAuthStore();
    const { mutate } = useSWRConfig();
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false);

    const [formData, setFormData] = useState<CustomerFormData>({
        name: '',
        code: '',
        phone: '',
        status: 'Active'
    });

    // Multi-contact state — first row is always primary
    const [contacts, setContacts] = useState<ContactRow[]>([
        { email: '', name: '', role: '', isPrimary: true, receivesNotifications: true }
    ]);
    const [contactErrors, setContactErrors] = useState<string[]>(['']);

    const [errors, setErrors] = useState<Partial<Record<keyof CustomerFormData, string>>>({});
    const [conflict, setConflict] = useState<{ id: string; name: string } | null>(null);

    const addContact = () => {
        setContacts(prev => [...prev, { email: '', name: '', role: '', isPrimary: false, receivesNotifications: true }]);
        setContactErrors(prev => [...prev, '']);
    };

    const removeContact = (idx: number) => {
        setContacts(prev => prev.filter((_, i) => i !== idx));
        setContactErrors(prev => prev.filter((_, i) => i !== idx));
    };

    const updateContact = (idx: number, field: keyof ContactRow, value: string | boolean) => {
        setContacts(prev => prev.map((c, i) => i === idx ? { ...c, [field]: value } : c));
        if (field === 'email') {
            setContactErrors(prev => prev.map((e, i) => i === idx ? '' : e));
        }
    };

    const validateContacts = (): boolean => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        const newErrs = contacts.map((c, idx) => {
            if (idx === 0 && !c.email.trim()) return ''; // Primary email optional (for backward compat)
            if (c.email.trim() && !emailRegex.test(c.email.trim())) return 'Invalid email format';
            return '';
        });
        setContactErrors(newErrs);
        return newErrs.every(e => !e);
    };

    const validate = (): boolean => {
        try {
            customerSchema.parse(formData);
            setErrors({});
        } catch (error) {
            if (error instanceof z.ZodError) {
                const fieldErrors: any = {};
                error.issues.forEach((err: any) => {
                    if (err.path[0]) fieldErrors[err.path[0]] = err.message;
                });
                setErrors(fieldErrors);
                return false;
            }
        }
        // Phone digit validation (if provided)
        const phoneErr = validatePhone(formData.phone);
        if (phoneErr) {
            setErrors(prev => ({ ...prev, phone: phoneErr }));
            return false;
        }
        return validateContacts();
    };

    const resetForm = () => {
        setFormData({ name: '', code: '', phone: '', status: 'Active' });
        setContacts([{ email: '', name: '', role: '', isPrimary: true, receivesNotifications: true }]);
        setContactErrors(['']);
        setErrors({});
        setConflict(null);
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setConflict(null);

        if (!validate()) return;

        setLoading(true);
        try {
            // Derive primary email for backward compatibility with customers.email column
            const primaryEmail = contacts[0]?.email.trim() || null;

            // Step 1: Create customer with legacy email field populated
            const res = await fetch('/api/customers', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                body: JSON.stringify({ ...formData, email: primaryEmail })
            });

            if (!res.ok) {
                const error = await res.json();
                if (res.status === 409 && error.code === 'CUSTOMER_DUPLICATE') {
                    setConflict({ id: error.conflictCustomerId, name: error.conflictValue });
                    toast.error('Customer already exists');
                } else {
                    toast.error(error.error || 'Failed to create customer');
                }
                return;
            }

            const newCustomer = await res.json();

            // Step 2: Create contacts for all non-empty email rows
            const validContacts = contacts.filter(c => c.email.trim());
            await Promise.all(validContacts.map((c, idx) =>
                fetch(`/api/customers/${newCustomer.id}/contacts`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                    body: JSON.stringify({
                        email: c.email.trim(),
                        name: c.name.trim() || null,
                        role: c.role || null,
                        isPrimary: idx === 0,
                        receivesNotifications: c.receivesNotifications,
                        // When master toggle is on, opt-in to all notification types by default.
                        // Users can fine-tune individual flags later in the Customer Drawer.
                        notifyPod: c.receivesNotifications,
                        notifyShortage: c.receivesNotifications,
                        notifyAppointment: c.receivesNotifications,
                        notifyDelay: c.receivesNotifications,
                        notifyFollowupPo: c.receivesNotifications,
                    })
                })
            ));

            toast.success('Customer created successfully');
            setOpen(false);
            resetForm();
            mutate('/api/customers');
            onSuccess?.();

        } catch (err) {
            console.error(err);
            toast.error('Network error');
        } finally {
            setLoading(false);
        }
    };

    const handleOpenExisting = () => {
        if (conflict && onOpenConflict) {
            setOpen(false);
            onOpenConflict(conflict.id);
        }
    };

    return (
        <>
            <button
                onClick={() => { setOpen(true); resetForm(); }}
                className="inline-flex items-center justify-center rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 bg-primary text-primary-foreground hover:bg-primary/90 h-10 px-4 py-2 shadow-sm"
            >
                <Plus className="mr-2 h-4 w-4" /> Add Customer
            </button>

            <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) resetForm(); }}>
                <DialogPortal>
                    <DialogOverlay />
                    <DialogContent className="sm:max-w-[500px] max-h-[90vh] flex flex-col overflow-hidden">
                        <DialogHeader className="shrink-0">
                            <DialogTitle className="flex items-center gap-2">
                                <Users className="h-5 w-5 text-primary" />
                                Add New Customer
                            </DialogTitle>
                        </DialogHeader>

                        <div className="overflow-y-auto flex-1 pr-1">
                            {conflict && (
                                <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg p-4 flex items-start gap-3 mb-4">
                                    <AlertCircle className="text-amber-500 shrink-0 mt-0.5" size={18} />
                                    <div className="space-y-2 w-full">
                                        <p className="text-sm font-medium text-amber-500">Duplicate Customer Found</p>
                                        <p className="text-xs text-muted-foreground">
                                            A customer named <span className="font-semibold text-foreground">"{conflict.name}"</span> already exists.
                                        </p>
                                        <button
                                            type="button"
                                            onClick={handleOpenExisting}
                                            className="text-xs bg-amber-500/10 hover:bg-amber-500/20 text-amber-500 font-medium px-3 py-1.5 rounded-md flex items-center gap-1 transition-colors w-fit"
                                        >
                                            Open Existing Record <ArrowRight size={12} />
                                        </button>
                                    </div>
                                </div>
                            )}

                            <form onSubmit={handleSubmit} className="space-y-4 py-2" id="customer-create-form">
                                {/* Name + Status */}
                                <div className="grid grid-cols-2 gap-4">
                                    <div className="space-y-2">
                                        <label htmlFor="cust-name" className="text-sm font-medium leading-none">
                                            Customer Name <span className="text-red-500">*</span>
                                        </label>
                                        <input
                                            id="cust-name"
                                            value={formData.name}
                                            onChange={(e) => {
                                                setFormData(prev => ({ ...prev, name: e.target.value }));
                                                if (errors.name) setErrors(prev => ({ ...prev, name: undefined }));
                                                if (conflict) setConflict(null);
                                            }}
                                            className={`flex h-10 w-full rounded-md border bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 ${errors.name ? 'border-red-500 focus-visible:ring-red-500' : 'border-input focus-visible:ring-primary'}`}
                                            placeholder="e.g. Target Corp"
                                        />
                                        {errors.name && <span className="text-xs text-red-500">{errors.name}</span>}
                                    </div>
                                    <div className="space-y-2">
                                        <label htmlFor="cust-status" className="text-sm font-medium leading-none">Status</label>
                                        <select
                                            id="cust-status"
                                            value={formData.status}
                                            onChange={(e) => setFormData(prev => ({ ...prev, status: e.target.value as any }))}
                                            className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
                                        >
                                            <option value="Active">Active</option>
                                            <option value="Inactive">Inactive</option>
                                        </select>
                                    </div>
                                </div>

                                {/* Code + Phone */}
                                <div className="grid grid-cols-2 gap-4">
                                    <div className="space-y-2">
                                        <label htmlFor="cust-code" className="text-sm font-medium leading-none">Customer Code</label>
                                        <input
                                            id="cust-code"
                                            value={formData.code}
                                            onChange={(e) => {
                                                setFormData(prev => ({ ...prev, code: e.target.value }));
                                                if (errors.code) setErrors(prev => ({ ...prev, code: undefined }));
                                            }}
                                            className={`flex h-10 w-full rounded-md border bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 ${errors.code ? 'border-red-500 focus-visible:ring-red-500' : 'border-input focus-visible:ring-primary'}`}
                                            placeholder="e.g. TGT-001"
                                        />
                                        {errors.code && <span className="text-xs text-red-500">{errors.code}</span>}
                                    </div>
                                    <div className="space-y-2">
                                        <label htmlFor="cust-phone" className="text-sm font-medium leading-none">Phone</label>
                                        <input
                                            id="cust-phone"
                                            value={formData.phone}
                                            onChange={(e) => {
                                                setFormData(prev => ({ ...prev, phone: e.target.value }));
                                                if (errors.phone) setErrors(prev => ({ ...prev, phone: undefined }));
                                            }}
                                            className={`flex h-10 w-full rounded-md border bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 ${errors.phone ? 'border-red-500 focus-visible:ring-red-500' : 'border-input focus-visible:ring-primary'}`}
                                            placeholder="(555) 123-4567"
                                        />
                                        {errors.phone && <span className="text-xs text-red-500">{errors.phone}</span>}
                                    </div>
                                </div>

                                {/* ── Contacts Section ── */}
                                <div className="space-y-3">
                                    <div className="flex items-center justify-between">
                                        <label className="text-sm font-medium leading-none flex items-center gap-1.5">
                                            <Mail size={14} className="text-muted-foreground" />
                                            Contacts
                                        </label>
                                        <span className="text-[10px] text-muted-foreground bg-muted px-2 py-0.5 rounded">
                                            First row is Primary
                                        </span>
                                    </div>

                                    <div className="space-y-2">
                                        {contacts.map((contact, idx) => (
                                            <div key={idx} className={`rounded-lg border p-3 space-y-2 ${idx === 0 ? 'border-primary/30 bg-primary/5' : 'border-input bg-background'}`}>
                                                {/* Row header */}
                                                <div className="flex items-center justify-between">
                                                    <span className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                                                        {idx === 0 ? '★ Primary Contact' : `Contact ${idx + 1}`}
                                                    </span>
                                                    {idx > 0 && (
                                                        <button
                                                            type="button"
                                                            onClick={() => removeContact(idx)}
                                                            className="text-red-400 hover:text-red-500 transition-colors"
                                                            aria-label="Remove contact"
                                                        >
                                                            <Trash2 size={13} />
                                                        </button>
                                                    )}
                                                </div>

                                                {/* Email (required for row 0 only if filled) */}
                                                <input
                                                    type="email"
                                                    value={contact.email}
                                                    onChange={(e) => updateContact(idx, 'email', e.target.value)}
                                                    placeholder={idx === 0 ? 'accounts@example.com' : 'Email address'}
                                                    className={`flex h-9 w-full rounded-md border bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 ${contactErrors[idx] ? 'border-red-500 focus-visible:ring-red-500' : 'border-input focus-visible:ring-primary'}`}
                                                />
                                                {contactErrors[idx] && (
                                                    <span className="text-xs text-red-500">{contactErrors[idx]}</span>
                                                )}

                                                {/* Name + Role (compact row) */}
                                                <div className="grid grid-cols-2 gap-2">
                                                    <input
                                                        type="text"
                                                        value={contact.name}
                                                        onChange={(e) => updateContact(idx, 'name', e.target.value)}
                                                        placeholder="Name (optional)"
                                                        className="flex h-8 w-full rounded-md border border-input bg-background px-3 py-1.5 text-xs focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary focus-visible:ring-offset-1"
                                                    />
                                                    <select
                                                        value={contact.role}
                                                        onChange={(e) => updateContact(idx, 'role', e.target.value)}
                                                        className="flex h-8 w-full rounded-md border border-input bg-background px-2 py-1.5 text-xs focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary focus-visible:ring-offset-1"
                                                    >
                                                        {CONTACT_ROLES.map(r => (
                                                            <option key={r.value} value={r.value}>{r.label}</option>
                                                        ))}
                                                    </select>
                                                </div>

                                                {/* Receives notifications toggle */}
                                                <label className="flex items-center gap-2 cursor-pointer">
                                                    <input
                                                        type="checkbox"
                                                        checked={contact.receivesNotifications}
                                                        onChange={(e) => updateContact(idx, 'receivesNotifications', e.target.checked)}
                                                        className="h-3.5 w-3.5 rounded border-input accent-primary"
                                                    />
                                                    <span className="text-[11px] text-muted-foreground">Receives notifications</span>
                                                </label>
                                            </div>
                                        ))}
                                    </div>

                                    <button
                                        type="button"
                                        onClick={addContact}
                                        className="flex items-center gap-1.5 text-xs text-primary hover:text-primary/80 font-medium transition-colors"
                                    >
                                        <Plus size={13} /> Add another contact
                                    </button>
                                </div>
                            </form>
                        </div>

                        <DialogFooter className="shrink-0 mt-4">
                            <button
                                type="button"
                                onClick={() => { setOpen(false); resetForm(); }}
                                className="inline-flex items-center justify-center rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 border border-input bg-background hover:bg-accent hover:text-accent-foreground h-10 px-4 py-2 mr-2"
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                form="customer-create-form"
                                disabled={loading}
                                className="inline-flex items-center justify-center rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 bg-primary text-primary-foreground hover:bg-primary/90 h-10 px-4 py-2"
                            >
                                {loading ? (
                                    <>
                                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                        Creating...
                                    </>
                                ) : 'Create Customer'}
                            </button>
                        </DialogFooter>
                    </DialogContent>
                </DialogPortal>
            </Dialog>
        </>
    );
}
